import React, { useState, useEffect, useMemo } from 'react';
// import { appInsights, reactPlugin } from '../../../appInsights';
// import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { SelectChangeEvent } from '@mui/material';
import { TextComponent, SelectComponent } from '../../uiComponents';
import Button from '@mui/material/Button';
import { gritComponentStyles } from '../gritComponentStyles';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import { useSelector } from 'react-redux';
import { getOtherProfilesDropdownList, getClaims, getOtherProfiles } from '../../../store';
import { PreConditionsComponent } from '../../gritComponents';
import { init_ValidationTP } from '../../../xmlParsing/initInterfaces';
import { ValidationTP } from '../../../xmlParsing/interfaces';
import { OptionProps, ValidationTPUI } from '../../../view-models';
import isEqual from 'lodash/isEqual';
import { convertIntoObject } from '../../../utils/mergeUtils';

interface ValidationTPAddComponentProps {
  data: ValidationTPUI;
  list: ValidationTP[];
  mode: 'edit' | 'add';
  changeHandler: any;
  // openProfile: any;
}

function ValidationTPAddComponent({ data, list, mode, changeHandler }: ValidationTPAddComponentProps) {
  const [origData, setOrigData] = useState<ValidationTPUI>({ ...init_ValidationTP(), vtpType: 'REST-API' });
  const [modifiedData, setModifiedData] = useState<ValidationTPUI>({ ...init_ValidationTP(), vtpType: 'REST-API' });
  const [hasError, setError] = useState<boolean>(false);
  const [vTPError, setVTPError] = useState<string>('Should not be empty');
  const [newSelection, setNewSelection] = useState<string>('selectExisting');
  const [filteredList, setFilteredList] = useState<OptionProps[]>([]);
  const otherProfilesDropdownList = useSelector(getOtherProfilesDropdownList);
  const allOtherProfiles = useSelector(getOtherProfiles);
  const classes = gritComponentStyles();

  useMemo(() => {
    setOrigData(data);
  }, [data]);

  useEffect(() => {
    isEqual(origData, modifiedData) ? setError(true) : setError(false);
  }, [modifiedData]);

  useEffect(() => {
    if (mode == 'add') setModifiedData({ ...init_ValidationTP(), vtpType: 'REST-API' });
    if (mode == 'edit') setModifiedData(data);
    if (newSelection === 'selectExisting' && otherProfilesDropdownList.length > 0) {
      const availableList = (list || []).reduce((acc: string[], curVal: ValidationTP) => acc.concat([curVal.id]), []);
      const _temp = otherProfilesDropdownList
        .filter((el: OptionProps) => !availableList.includes(el.value))
        .filter((el: OptionProps) => {
          const firstProtcol = allOtherProfiles[el.value]['protocol'][0];
          const secProtcol = allOtherProfiles[el.value]['protocol'][1];
          return (
            firstProtcol.toLowerCase().includes('openidconnect') ||
            (secProtcol.toLowerCase().includes('azureactivedirectory') &&
              convertIntoObject(allOtherProfiles[el.value]['metadata']).hasOwnProperty('Operation')) ||
            secProtcol.toLowerCase().includes('restful')
          );
        })
        .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1));
      setFilteredList(_temp);
      _temp.length === 0 ? setError(true) : setError(false);
    }
    if (newSelection === 'addNew') {
      setError(true);
    }
  }, [data, list, mode, newSelection, otherProfilesDropdownList]);

  useEffect(() => {
    filteredList.length > 0 && mode == 'add' && setModifiedData({ ...modifiedData, id: filteredList[0].value });
  }, [filteredList]);

  return (
    <>
      {mode === 'add' && (
        <div id="add-claimTypeReferenceId">
          <FormControl>
            <RadioGroup
              row
              value={newSelection}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setNewSelection(e.target.value);
              }}
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
            >
              <FormControlLabel
                value="selectExisting"
                control={<Radio />}
                label="Select from existing Validation Technical Profiles"
              />
              <FormControlLabel value="addNew" control={<Radio />} label="Add a new Validation Technical Profile" />
            </RadioGroup>
          </FormControl>
          <hr />
        </div>
      )}
      {mode === 'edit' && (
        <div id="edit-claimTypeReferenceId" className={classes.flexRow}>
          <h3>claimTypeReferenceId : {data.id}</h3>
        </div>
      )}
      {modifiedData && (
        <div className={classes.flexCol}>
          <div>
            {mode === 'add' && newSelection === 'selectExisting' && (
              <>
                {filteredList.length > 0 && (
                  <SelectComponent
                    changeHandler={(e: SelectChangeEvent<string>) => {
                      setModifiedData({ ...modifiedData, id: e.target.value });
                    }}
                    data={{
                      value: modifiedData['id'] || filteredList[0]['value'],
                      label: 'Select ValidationTechnicalProfileId',
                      options: filteredList,
                    }}
                  />
                )}
                {filteredList.length === 0 && (
                  <div>
                    There are no existing Validation Technical Profiles. Please add a new Validation Technical Profile.
                  </div>
                )}
              </>
            )}
            {mode === 'add' && newSelection === 'addNew' && (
              <>
                <TextComponent
                  changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                    setModifiedData({
                      ...modifiedData,
                      id: e.target.value,
                    });
                    if (e.target.value === '') {
                      setError(true);
                      setVTPError('Should not be empty');
                    } else if (!/^[a-zA-Z0-9]+[a-zA-Z0-9_-]{0,}$/.test(e.target.value)) {
                      setVTPError(
                        'Should begin with an alphabet or number and can contain alphabets, numbers and the following symbols: _ -',
                      );
                    } else if (
                      Object.keys(allOtherProfiles).findIndex(
                        (el: string) => el.trim().toLowerCase() === e.target.value.trim().toLowerCase(),
                      ) >= 0
                    ) {
                      setError(true);
                      setVTPError('A validation technical profile exists with the same Id');
                    } else {
                      setError(false);
                      setVTPError('');
                    }
                  }}
                  data={{
                    value: modifiedData['id'] || '',
                    label: 'New Validation Technical Profile Id',
                    error: !!vTPError,
                    helperText: vTPError,
                  }}
                />
                <SelectComponent
                  changeHandler={(e: SelectChangeEvent<any>) => {
                    setModifiedData({ ...modifiedData, vtpType: e.target.value });
                  }}
                  data={{
                    value: modifiedData['vtpType'],
                    label: 'Technical Profile Type',
                    options: [
                      { label: 'REST-API', value: 'REST-API' },
                      { label: 'AAD-Read', value: 'AAD-Read' },
                      { label: 'AAD-Write', value: 'AAD-Write' },
                    ],
                  }}
                />
              </>
            )}
            <div>
              <SelectComponent
                changeHandler={(e: SelectChangeEvent<string>) => {
                  setModifiedData({ ...modifiedData, continueOnError: e.target.value === 'true' ? true : false });
                }}
                data={{
                  value: modifiedData['continueOnError'].toString(),
                  label: 'continueOnError',
                  options: [
                    { label: 'false', value: 'false' },
                    { label: 'true', value: 'true' },
                  ],
                }}
              />
              <SelectComponent
                changeHandler={(e: SelectChangeEvent<string>) => {
                  setModifiedData({ ...modifiedData, continueOnSuccess: e.target.value === 'true' ? true : false });
                }}
                data={{
                  value: modifiedData['continueOnSuccess'].toString(),
                  label: 'continueOnSuccess',
                  options: [
                    { label: 'false', value: 'false' },
                    { label: 'true', value: 'true' },
                  ],
                }}
              />
            </div>

            <PreConditionsComponent
              data={modifiedData['preconditions']}
              type="validationTP"
              addHandler={(data: any) => {
                const _preC = modifiedData['preconditions'].concat([data]);
                setModifiedData({ ...modifiedData, preconditions: _preC });
              }}
              deleteHandler={(data: any) => {
                setModifiedData({ ...modifiedData, preconditions: data });
              }}
            />
          </div>
          <footer className={classes.footer}>
            <Button
              variant="contained"
              color="primary"
              disabled={hasError}
              onClick={() => {
                changeHandler(modifiedData);
              }}
            >
              {mode === 'add' ? 'Add' : 'Update'}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                changeHandler(null);
              }}
            >
              Cancel
            </Button>
          </footer>
        </div>
      )}
    </>
  );
}

// export default withAITracking(reactPlugin, ValidationTPAddComponent);
export default ValidationTPAddComponent;
